/**
 * Archivage PERSONNEL d'un rendez-vous (croix sur la carte, migration 0240) :
 * retire la réservation du tableau de bord de CE participant (tuteur ou
 * élève) uniquement — sans affecter l'autre partie ni la réservation
 * elle-même. Le rendez-vous reste consultable dans "Rendez-vous passés".
 * Table postérieure aux types générés (`types.generated.ts` n'est jamais
 * régénéré, cf. CLAUDE.md) → client générique.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

/** Fetch brut, réutilisé par les hooks "à venir"/"passés" côté élève pour combiner en un seul aller-retour. */
export async function fetchArchivedReservationIds(userId: string): Promise<Set<string>> {
  const { data, error } = await db
    .from('tutor_booking_reservation_archives')
    .select('reservation_id')
    .eq('user_id', userId);
  if (error) throw new Error(error.message);
  return new Set((data ?? []).map((r: { reservation_id: string }) => r.reservation_id));
}

/** Ids de rendez-vous archivés PAR L'UTILISATEUR COURANT — à exclure des listes "à venir" côté tuteur. */
export function useMyArchivedRendezVousIds(userId: string | undefined) {
  return useQuery({
    queryKey: ['rendezvous-archived-ids', userId],
    enabled: !!userId,
    queryFn: () => fetchArchivedReservationIds(userId!),
  });
}

function invalidateAfterArchiveChange(
  qc: ReturnType<typeof useQueryClient>,
  userId: string | undefined,
) {
  qc.invalidateQueries({ queryKey: ['rendezvous-archived-ids', userId] });
  qc.invalidateQueries({ queryKey: ['my-upcoming-bookings', userId] });
  qc.invalidateQueries({ queryKey: ['my-past-bookings', userId] });
  qc.invalidateQueries({ queryKey: ['tutor-booking-slots', userId] });
}

/** Archive un rendez-vous pour l'utilisateur courant seulement (insert). */
export function useArchiveRendezVous(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reservationId: string) => {
      if (!userId) throw new Error('missing userId');
      const { error } = await db
        .from('tutor_booking_reservation_archives')
        .insert({ reservation_id: reservationId, user_id: userId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateAfterArchiveChange(qc, userId),
  });
}

/** Désarchive un rendez-vous (bouton "Restaurer" depuis "Rendez-vous passés"). */
export function useUnarchiveRendezVous(userId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reservationId: string) => {
      if (!userId) throw new Error('missing userId');
      const { error } = await db
        .from('tutor_booking_reservation_archives')
        .delete()
        .eq('reservation_id', reservationId)
        .eq('user_id', userId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateAfterArchiveChange(qc, userId),
  });
}
